import {
  Client,
  cacheExchange,
  fetchExchange,
  type Exchange,
  type Operation,
} from "@urql/vue";
import { map, pipe } from "wonka";
import { getSharedHeaders } from "./headers";
import { createApqExchange } from "./urql-apq-exchange";
import { cacheClassificationExchange } from "./urql-cache-policy";
import { shallowRef } from "vue";

export const graphQlClientProvideKey = "$urql";

/**
 * Extracts operation name from a GraphQL operation AST.
 */
export function getOperationName(operation: Operation): string {
  if (!("definitions" in operation.query)) return "anonymous";
  for (const definition of operation.query.definitions) {
    if (
      definition.kind === "OperationDefinition" &&
      definition.name &&
      typeof definition.name.value === "string"
    ) {
      return definition.name.value;
    }
  }
  return "anonymous";
}

/**
 * Custom exchange that appends `?op=operationName` to the URL
 */
const opParamExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    return pipe(
      ops$,
      map((operation: Operation) => {
        const originalUrl = import.meta.env.VITE_GRAPHQL_URL;
        const url = new URL(originalUrl, window.location.origin);

        const opName = getOperationName(operation);
        url.searchParams.set("opName", opName);

        operation = {
          ...operation,
          context: {
            ...operation.context,
            url: url.toString(),
          },
        };

        return operation;
      }),
      forward,
    );
  };

function createGraphqlClient(): Client {
  return new Client({
    url: import.meta.env.VITE_GRAPHQL_URL,
    exchanges: [
      cacheExchange,
      opParamExchange,
      cacheClassificationExchange,
      createApqExchange(),
      fetchExchange,
    ],
    requestPolicy: "cache-and-network",
    fetchOptions: (): RequestInit => ({
      headers: getSharedHeaders(),
    }),
  });
}

export const graphQlClient = shallowRef<Client>(createGraphqlClient());

export function resetGraphqlClient(): void {
  graphQlClient.value = createGraphqlClient();
}
